import React from 'react';
import {
  IconFileText,
  IconChevronDown,
  IconSparkles,
  IconX,
  IconSearch,
} from '../../credit-plans/SVGIcons';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
} from '../ui';
import { fetchImportDatabaseListItemsByGroupId } from '../lib/import-service';
import { useImportHubReadOnly } from '../lib/access';
import { toast } from '../lib/toast';
import { GlobalContext } from '../../../providers/GlobalProvider';
import SopRulesAPI, { isAbortError } from '../../../api/SopRulesAPI';
import type {
  SopExtraction,
  SopExtractionStage,
  SopRule,
  SopRuleCondition,
} from '../../../api/SopRulesAPI';
import {
  copyImportRuleset,
  fetchImportRulesets,
  fetchRulesetSourceExtraction,
  formatRulesetDay,
  RULESET_JOB_VERBS,
  manualChangeNote,
  rulesetStatusParts,
  saveImportRuleset,
} from '../lib/ruleset-service';
import { useRulesetJob } from '../lib/use-ruleset-job';
import RulesetChangesDialog from './RulesetChangesDialog';
import type { ImportRuleset, RulesetJobKind } from '../lib/ruleset-service';
import './sop.css';

const RULES_PER_PAGE = 5;

// Matches pyapi's upload checks (services/sop_rules/service.py), so an
// unsupported file is caught before it is uploaded.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];

// The extraction job's stages, in the order pyapi runs them.
const ANALYSIS_STEPS: { stage: SopExtractionStage; label: string }[] = [
  { stage: 'reading', label: 'Reading document' },
  { stage: 'finding_sections', label: 'Finding procedures' },
  { stage: 'extracting', label: 'Extracting rules' },
  { stage: 'verifying', label: 'Checking sources' },
];

type StatusFilter = 'all' | 'selected' | 'unselected';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'Any status' },
  { key: 'selected', label: 'Selected' },
  { key: 'unselected', label: 'Not selected' },
];

type Status = 'empty' | 'analyzing' | 'done' | 'error';

type PendingApply = {
  mode: 'new' | 'update' | 'apply';
  /** Unset for a ruleset that is saved by confirming. */
  rulesetId?: string;
  name: string;
  ruleCount: number;
};

type PendingRemoval = {
  action: 'deactivate' | 'delete';
  ruleset: ImportRuleset;
};

// Rule ids (r1, r2, …) only mean something within the extraction that
// produced them, so a saved ruleset can be loaded back only into that one.
const ruleIdsOf = (ruleset: ImportRuleset) =>
  ruleset.rules
    .map((rule) => rule.source_rule_id)
    .filter((id): id is string => !!id);

const ACTION_VERBS: Record<SopRule['action']['type'], string> = {
  enable_fta: 'Claim',
  disable_fta: "Don't claim",
  enable_exclusion: 'Claim',
  disable_exclusion: "Don't claim",
  none: '',
};

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const sameRuleIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

const describeCondition = (condition: SopRuleCondition): string => {
  const values = condition.values.join(', ');
  if (condition.field === 'hts_code') return `HTS starts with ${values}`;
  const label = condition.field === 'country_of_origin' ? 'Origin' : 'Category';
  return condition.operator === 'not_in'
    ? `${label} not ${values}`
    : `${label}: ${values}`;
};

const sourceLabel = (rule: SopRule) =>
  [rule.section, rule.page ? `p. ${rule.page}` : '']
    .filter(Boolean)
    .join(' · ');

const fileProblem = (file: File): string | null => {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) {
    return 'Upload the SOP as a PDF or Word (.docx) file.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return `The SOP is over the ${MAX_UPLOAD_BYTES / (1024 * 1024)} MB limit.`;
  }
  return null;
};

const ruleMatchesQuery = (rule: SopRule, q: string) =>
  [
    rule.title,
    rule.detail,
    rule.section,
    rule.quote,
    rule.action.code_label,
    ...rule.conditions.map(describeCondition),
  ].some((text) => text.toLowerCase().includes(q));

const SourceToggle = ({
  rule,
  open,
  onToggle,
}: {
  rule: SopRule;
  open: boolean;
  onToggle: () => void;
}): JSX.Element => {
  const source = sourceLabel(rule);
  return (
    <>
      <button
        type='button'
        className={`sop-quote-toggle${open ? ' is-open' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
      >
        <IconChevronDown />
        {open ? 'Hide source' : 'View source'}
        {source ? ` · ${source}` : ''}
      </button>
      {open && <div className='sop-quote'>“{rule.quote}”</div>}
    </>
  );
};

// A database has at most one active ruleset; applying another replaces it.
const SOP = ({
  databaseId,
  databaseName,
}: {
  databaseId?: string;
  databaseName?: string;
}): JSX.Element => {
  const readOnly = useImportHubReadOnly();
  const { cur_user } = React.useContext(GlobalContext);
  const groupId: string | undefined = cur_user?.selected_team?.id;
  const target = databaseName ?? 'this database';
  const [file, setFile] = React.useState<File | null>(null);
  const [status, setStatus] = React.useState<Status>('empty');
  const [extraction, setExtraction] = React.useState<SopExtraction | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);
  // Rules picked in the editor; nothing reaches the database until a ruleset
  // is saved and applied.
  const [selected, setSelected] = React.useState<Record<string, boolean>>({});
  const [openQuote, setOpenQuote] = React.useState<string | null>(null);
  const [otherOpen, setOtherOpen] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const [page, setPage] = React.useState(1);
  const [saveName, setSaveName] = React.useState('');
  const [rulesets, setRulesets] = React.useState<ImportRuleset[]>([]);
  const [rulesetsLoading, setRulesetsLoading] = React.useState(true);
  const [rulesetsError, setRulesetsError] = React.useState<string | null>(null);
  // The saved ruleset the editor is working on (after Load, or once a new
  // ruleset is saved). Save and Apply updates it in place instead of adding a
  // new row.
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [pendingApply, setPendingApply] = React.useState<PendingApply | null>(
    null,
  );
  const [pendingRemoval, setPendingRemoval] =
    React.useState<PendingRemoval | null>(null);
  // Save and Apply is busy from the save request on, not just once its job
  // starts, so a second confirmation during a slow save can't create another
  // ruleset. The ref also stops two confirmations in the same render.
  const [saving, setSaving] = React.useState(false);
  const submittingRef = React.useRef(false);
  const [loadingRulesetId, setLoadingRulesetId] = React.useState<string | null>(
    null,
  );
  // "Copy to…": the ruleset being copied and the team's other databases.
  const [copySource, setCopySource] = React.useState<ImportRuleset | null>(
    null,
  );
  const [changesRuleset, setChangesRuleset] =
    React.useState<ImportRuleset | null>(null);
  const [copyDatabases, setCopyDatabases] = React.useState<Array<{
    id: string;
    name: string;
  }> | null>(null);
  const [copyTargetId, setCopyTargetId] = React.useState('');
  const [copyError, setCopyError] = React.useState<string | null>(null);
  const [copying, setCopying] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const filtersRef = React.useRef<HTMLDivElement>(null);
  // The in-flight upload + poll; aborted when a new file replaces it, the
  // file is removed, or the page unmounts.
  const jobRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => () => jobRef.current?.abort(), []);

  const loadRulesets = React.useCallback(async () => {
    if (!databaseId) {
      setRulesetsLoading(false);
      return;
    }
    const loaded = await fetchImportRulesets(databaseId);
    setRulesetsLoading(false);
    if (loaded.success) {
      setRulesets(loaded.rulesets);
      setRulesetsError(null);
    } else {
      setRulesetsError(loaded.error ?? 'Failed to load rulesets');
    }
  }, [databaseId]);

  React.useEffect(() => {
    setRulesetsLoading(true);
    void loadRulesets();
  }, [loadRulesets]);

  const rulesetJob = useRulesetJob(databaseId, target, loadRulesets);
  const { runningJob } = rulesetJob;

  const extractionId = extraction?.extraction_id ?? null;
  const applicableRules = React.useMemo(
    () => (extraction?.result?.rules ?? []).filter((r) => r.applicable),
    [extraction],
  );
  const otherRules = React.useMemo(
    () => (extraction?.result?.rules ?? []).filter((r) => !r.applicable),
    [extraction],
  );
  const warnings = extraction?.result?.warnings ?? [];

  const q = query.trim().toLowerCase();
  const visibleRules = applicableRules.filter(
    (r) =>
      (!q || ruleMatchesQuery(r, q)) &&
      (statusFilter === 'all' ||
        (statusFilter === 'selected' ? !!selected[r.id] : !selected[r.id])),
  );

  const visibleOtherRules = q
    ? otherRules.filter((r) => ruleMatchesQuery(r, q))
    : otherRules;
  const otherMatchCount = visibleOtherRules.length;

  React.useEffect(() => {
    if (q && otherMatchCount > 0) setOtherOpen(true);
  }, [q, otherMatchCount]);

  const pageCount = Math.max(
    1,
    Math.ceil(visibleRules.length / RULES_PER_PAGE),
  );
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * RULES_PER_PAGE;
  const pageRules = visibleRules.slice(pageStart, pageStart + RULES_PER_PAGE);

  React.useEffect(() => {
    setPage((previous) => Math.min(previous, pageCount));
  }, [pageCount]);

  const changePage = (next: number) => {
    setPage(next);
    setOpenQuote(null);
    filtersRef.current?.scrollIntoView({ block: 'start' });
  };

  const startAnalysis = async (f: File) => {
    jobRef.current?.abort();
    jobRef.current = null;
    setFile(f);
    setExtraction(null);
    setSelected({});
    setEditingId(null);
    setSaveName(f.name.replace(/\.[^.]+$/, ''));
    setOpenQuote(null);
    setOtherOpen(false);
    setQuery('');
    setStatusFilter('all');
    setPage(1);

    const problem =
      fileProblem(f) ??
      (!groupId || !databaseId
        ? 'Open a database in your team before uploading an SOP.'
        : null);
    if (problem || !groupId || !databaseId) {
      setError(problem);
      setStatus('error');
      return;
    }

    const controller = new AbortController();
    jobRef.current = controller;
    setError(null);
    setStatus('analyzing');
    try {
      const created = await SopRulesAPI.createExtraction(
        f,
        groupId,
        databaseId,
        controller.signal,
      );
      setExtraction(created);
      const finished = await SopRulesAPI.pollExtraction(
        created.extraction_id,
        groupId,
        { signal: controller.signal, onUpdate: setExtraction },
      );
      setExtraction(finished);
      setStatus('done');
    } catch (err) {
      if (isAbortError(err)) return;
      setError(err instanceof Error ? err.message : 'Rule extraction failed.');
      setStatus('error');
    } finally {
      if (jobRef.current === controller) jobRef.current = null;
    }
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void startAnalysis(f);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f && !readOnly) void startAnalysis(f);
  };

  const reset = () => {
    jobRef.current?.abort();
    jobRef.current = null;
    setFile(null);
    setStatus('empty');
    setExtraction(null);
    setError(null);
    setSelected({});
    setEditingId(null);
    setOpenQuote(null);
  };

  const selectedIds = applicableRules
    .filter((r) => selected[r.id])
    .map((r) => r.id);
  const selectedCount = selectedIds.length;
  const allSelected =
    applicableRules.length > 0 && selectedCount === applicableRules.length;
  const activeRuleset = rulesets.find((r) => r.is_active) ?? null;
  const editingRuleset = rulesets.find((r) => r.id === editingId) ?? null;
  const activeHere =
    activeRuleset && activeRuleset.source_extraction_id === extractionId
      ? activeRuleset
      : null;
  // Nothing to save: the editor matches the ruleset already active on the
  // database (including its name when editing a saved ruleset).
  const isUpToDate = editingRuleset
    ? editingRuleset.is_active &&
      sameRuleIds(selectedIds, ruleIdsOf(editingRuleset)) &&
      saveName.trim() === editingRuleset.name
    : !!activeHere && sameRuleIds(selectedIds, ruleIdsOf(activeHere));
  const busy = saving || runningJob !== null;

  const requestSaveAndApply = () =>
    setPendingApply({
      mode: editingRuleset ? 'update' : 'new',
      rulesetId: editingRuleset?.id,
      name: saveName.trim(),
      ruleCount: selectedCount,
    });

  /** Start an apply / turn-off / delete job and follow it to the end. */
  const runRulesetJob = async (
    rulesetId: string,
    action: RulesetJobKind,
    name: string,
  ) => {
    const done = await rulesetJob.run(rulesetId, action, name);
    if (done && action === 'delete') {
      setEditingId((current) => (current === rulesetId ? null : current));
    }
  };

  const confirmApply = async () => {
    if (!pendingApply || !databaseId || submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    const request = pendingApply;
    setPendingApply(null);
    try {
      let rulesetId = request.rulesetId;
      if (request.mode !== 'apply') {
        const saved = await saveImportRuleset(databaseId, {
          rulesetId,
          name: request.name,
          sourceFilename: file?.name ?? extraction?.filename ?? null,
          sourceExtractionId: extractionId || null,
          extraction,
          rules: applicableRules.filter((rule) => selected[rule.id]),
        });
        if (!saved.success || !saved.ruleset) {
          toast.error(saved.error ?? 'Failed to save the ruleset');
          return;
        }
        const ruleset = saved.ruleset;
        rulesetId = ruleset.id;
        setEditingId(ruleset.id);
        setRulesets((list) => [
          ruleset,
          ...list.filter((existing) => existing.id !== ruleset.id),
        ]);
      }
      if (rulesetId) await runRulesetJob(rulesetId, 'apply', request.name);
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  /**
   * Open a saved ruleset in the editor. When the SOP it came from isn't the one
   * on screen (e.g. after a refresh), it's rebuilt from the copy saved with the
   * ruleset. Rulesets saved before that copy existed fall back to pyapi, which
   * keeps extractions for 30 days.
   */
  const loadRuleset = async (ruleset: ImportRuleset) => {
    if (!databaseId) return;
    if (!extraction || (ruleset.source_extraction_id ?? '') !== extractionId) {
      setLoadingRulesetId(ruleset.id);
      let loaded: SopExtraction | null = null;
      try {
        loaded = await fetchRulesetSourceExtraction(databaseId, ruleset);
        if (!loaded && groupId && ruleset.source_extraction_id) {
          loaded = await SopRulesAPI.getExtraction(
            ruleset.source_extraction_id,
            groupId,
          );
        }
      } catch {
        loaded = null;
      } finally {
        setLoadingRulesetId(null);
      }
      if (!loaded || loaded.status !== 'complete' || !loaded.result) {
        toast.error(
          'The SOP this ruleset came from is no longer available. Upload it again to edit the ruleset.',
        );
        return;
      }
      jobRef.current?.abort();
      jobRef.current = null;
      setFile(null);
      setExtraction(loaded);
      setError(null);
      setStatus('done');
      setOpenQuote(null);
      setOtherOpen(false);
      setQuery('');
      setStatusFilter('all');
      setPage(1);
    }
    const next: Record<string, boolean> = {};
    ruleIdsOf(ruleset).forEach((id) => (next[id] = true));
    setSelected(next);
    setSaveName(ruleset.name);
    setEditingId(ruleset.id);
  };

  // An inactive ruleset has changed nothing, so it is deleted without asking;
  // an active one is undone first, which is worth confirming.
  const requestDelete = (ruleset: ImportRuleset) => {
    if (ruleset.is_active) setPendingRemoval({ action: 'delete', ruleset });
    else void runRulesetJob(ruleset.id, 'delete', ruleset.name);
  };

  const openCopy = async (ruleset: ImportRuleset) => {
    setCopySource(ruleset);
    setCopyError(null);
    if (!groupId) {
      setCopyError('Open a database in your team before copying a ruleset.');
      return;
    }
    if (copyDatabases) return;
    const loaded = await fetchImportDatabaseListItemsByGroupId(groupId);
    if (!loaded.success) {
      setCopyError(loaded.error ?? 'Failed to load databases');
      return;
    }
    const others = loaded.databases
      .filter((database) => database.id !== databaseId)
      .map((database) => ({ id: database.id, name: database.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setCopyDatabases(others);
    setCopyTargetId(others[0]?.id ?? '');
  };

  const confirmCopy = async () => {
    if (!copySource || !databaseId || !copyTargetId) return;
    setCopying(true);
    const copied = await copyImportRuleset(
      databaseId,
      copySource.id,
      copyTargetId,
    );
    setCopying(false);
    if (!copied.success || !copied.ruleset) {
      setCopyError(copied.error ?? 'Failed to copy the ruleset');
      return;
    }
    const targetName =
      copyDatabases?.find((database) => database.id === copyTargetId)?.name ??
      'the other database';
    setCopySource(null);
    toast.success(
      `Copied as "${copied.ruleset.name}" to ${targetName}. Apply it from that database's Database Rules page.`,
    );
  };

  const confirmRemoval = () => {
    if (!pendingRemoval) return;
    const { action, ruleset } = pendingRemoval;
    setPendingRemoval(null);
    void runRulesetJob(ruleset.id, action, ruleset.name);
  };

  const toggleAll = () => {
    const next: Record<string, boolean> = {};
    applicableRules.forEach((r) => (next[r.id] = !allSelected));
    setSelected(next);
  };

  const stepIndex = Math.max(
    0,
    ANALYSIS_STEPS.findIndex((s) => s.stage === extraction?.stage),
  );
  const chunksTotal = extraction?.progress.chunks_total ?? 0;
  const analyzingLabel = !extraction
    ? 'Uploading'
    : extraction.stage === 'extracting' && chunksTotal > 1
    ? `Extracting rules (${extraction.progress.chunks_done} of ${chunksTotal} parts)`
    : ANALYSIS_STEPS[stepIndex].label;
  const pageCountLabel = extraction?.result?.page_count
    ? plural(extraction.result.page_count, 'page')
    : '';

  return (
    <div className='sop-root'>
      <div className='sop-header'>
        <div className='sop-title'>Trade Agreements</div>
        <div className='sop-subtitle'>
          Upload a Standard Operating Procedure and our agent will find the
          rules BITE can apply to this database, like which free trade
          agreements and tariff exclusions to claim.
        </div>
      </div>

      <input
        ref={inputRef}
        type='file'
        accept={ACCEPTED_EXTENSIONS.join(',')}
        className='sop-file-input'
        onChange={onPick}
      />

      {status === 'empty' ? (
        <div
          className={`sop-drop${dragging ? ' is-dragging' : ''}`}
          onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (!readOnly) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <div className='sop-drop-icon'>
            <IconFileText />
          </div>
          <div className='sop-drop-title'>Drop your SOP here</div>
          <div className='sop-drop-text'>
            PDF or Word (.docx), up to {MAX_UPLOAD_BYTES / (1024 * 1024)} MB
          </div>
          <button
            type='button'
            className='sop-btn-primary'
            disabled={readOnly}
            title={readOnly ? 'Requires a Business license' : undefined}
            onClick={() => inputRef.current?.click()}
          >
            Choose file
          </button>
        </div>
      ) : (
        <div className={`sop-file${status === 'error' ? ' is-error' : ''}`}>
          <div className='sop-file-icon'>
            <IconFileText />
          </div>
          <div className='sop-file-text'>
            <div className='sop-file-name'>
              {file?.name ?? extraction?.filename}
            </div>
            <div className='sop-file-meta'>
              {[
                file && formatSize(file.size),
                status === 'analyzing' && `${analyzingLabel}…`,
                status === 'done' && pageCountLabel,
                status === 'done' &&
                  `${applicableRules.length} applicable, ${plural(
                    otherRules.length,
                    'other rule',
                  )}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {status === 'error' && error && (
              <div className='sop-file-error' role='alert'>
                {error}
              </div>
            )}
          </div>
          {status === 'analyzing' ? (
            <div className='sop-spinner' />
          ) : (
            <>
              {status === 'error' && file && !fileProblem(file) && (
                <button
                  type='button'
                  className='sop-btn-ghost'
                  onClick={() => void startAnalysis(file)}
                >
                  Try again
                </button>
              )}
              <button
                type='button'
                className='sop-btn-ghost'
                disabled={readOnly}
                onClick={() => inputRef.current?.click()}
              >
                Replace
              </button>
            </>
          )}
          <button
            type='button'
            className='sop-icon-btn'
            onClick={reset}
            title='Remove'
          >
            <IconX />
          </button>
        </div>
      )}

      {status === 'analyzing' && (
        <div className='sop-progress'>
          {ANALYSIS_STEPS.map((s, i) => (
            <div
              key={s.stage}
              className={`sop-progress-step${
                extraction && i < stepIndex
                  ? ' is-done'
                  : i === stepIndex
                  ? ' is-current'
                  : ''
              }`}
            >
              <span className='sop-progress-dot' />
              {s.label}
            </div>
          ))}
        </div>
      )}

      {status === 'done' && warnings.length > 0 && (
        <ul className='sop-warnings'>
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      {status === 'done' && (
        <div className='sop-rules'>
          <div className='sop-rules-head'>
            <div className='sop-rules-title'>
              <IconSparkles />
              Applicable rules we found
            </div>
            {applicableRules.length > 0 && (
              <div className='sop-rules-meta'>
                {selectedCount} of {applicableRules.length} selected
                <button
                  type='button'
                  className='sop-btn-ghost'
                  onClick={toggleAll}
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              </div>
            )}
          </div>

          {applicableRules.length === 0 ? (
            <div className='sop-empty'>
              None of the rules in this SOP set a free trade agreement or tariff
              exclusion BITE can apply.
            </div>
          ) : (
            <>
              <div className='sop-filters' ref={filtersRef}>
                <div className='sop-search'>
                  <IconSearch />
                  <input
                    className='sop-search-input'
                    placeholder='Search rules'
                    value={query}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setQuery(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <div className='sop-chips'>
                  {STATUS_FILTERS.map((s) => (
                    <button
                      key={s.key}
                      type='button'
                      className={`sop-chip${
                        statusFilter === s.key ? ' is-active' : ''
                      }`}
                      onClick={() => {
                        setStatusFilter(s.key);
                        setPage(1);
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className='sop-rule-list'>
                {pageRules.map((r) => {
                  const isOn = !!selected[r.id];
                  const isOpen = openQuote === r.id;
                  return (
                    <div
                      key={r.id}
                      className={`sop-rule${isOn ? ' is-selected' : ''}`}
                    >
                      <div className='sop-rule-main'>
                        <div className='sop-rule-text'>
                          <div className='sop-rule-title'>{r.title}</div>
                          <div className='sop-rule-detail'>{r.detail}</div>
                          <div className='sop-rule-tags'>
                            <span className='sop-tag is-program'>
                              {ACTION_VERBS[r.action.type]} ·{' '}
                              {r.action.code_label}
                            </span>
                            {r.conditions.length === 0 ? (
                              <span className='sop-tag'>All products</span>
                            ) : (
                              r.conditions.map((c) => (
                                <span
                                  key={`${c.field}-${
                                    c.operator
                                  }-${c.values.join()}`}
                                  className='sop-tag'
                                >
                                  {describeCondition(c)}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                        <button
                          type='button'
                          role='switch'
                          aria-checked={isOn}
                          aria-label={`${isOn ? 'Deselect' : 'Select'} ${
                            r.title
                          }`}
                          className={`sop-switch${isOn ? ' is-on' : ''}`}
                          onClick={() =>
                            setSelected((a) => ({ ...a, [r.id]: !a[r.id] }))
                          }
                        >
                          <span className='sop-switch-knob' />
                        </button>
                      </div>
                      <SourceToggle
                        rule={r}
                        open={isOpen}
                        onToggle={() => setOpenQuote(isOpen ? null : r.id)}
                      />
                    </div>
                  );
                })}
                {visibleRules.length === 0 && (
                  <div className='sop-empty'>No rules match these filters.</div>
                )}
              </div>

              {visibleRules.length > 0 && (
                <div className='sop-pagination'>
                  <div className='sop-pagination-count' aria-live='polite'>
                    {pageStart + 1}–
                    {Math.min(pageStart + RULES_PER_PAGE, visibleRules.length)}{' '}
                    of {visibleRules.length} rules
                  </div>
                  {pageCount > 1 && (
                    <div className='sop-pagination-controls'>
                      <button
                        type='button'
                        className='sop-page-prev'
                        aria-label='Previous rules page'
                        title='Previous page'
                        disabled={currentPage === 1}
                        onClick={() => changePage(currentPage - 1)}
                      >
                        <IconChevronDown />
                      </button>
                      <span className='sop-page-label'>
                        Page {currentPage} of {pageCount}
                      </span>
                      <button
                        type='button'
                        className='sop-page-next'
                        aria-label='Next rules page'
                        title='Next page'
                        disabled={currentPage === pageCount}
                        onClick={() => changePage(currentPage + 1)}
                      >
                        <IconChevronDown />
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div
                className={`sop-action-bar${isUpToDate ? ' is-active' : ''}`}
              >
                <div className='sop-action-summary'>
                  <div className='sop-action-count'>
                    {selectedCount === 0
                      ? 'No rules selected'
                      : `${plural(selectedCount, 'rule')} selected`}
                  </div>
                  <div className='sop-action-hint'>
                    {busy
                      ? 'Wait for the current ruleset change to finish'
                      : isUpToDate
                      ? `Active on ${target}`
                      : selectedCount === 0
                      ? 'Select the rules you want to enforce'
                      : editingRuleset
                      ? `Editing “${editingRuleset.name}” · saving updates it and applies it to ${target}`
                      : `Saving applies these rules to products in ${target}`}
                  </div>
                </div>
                <input
                  className='sop-save-input'
                  placeholder='Name this ruleset, e.g. US import baseline'
                  aria-label='Ruleset name'
                  value={saveName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSaveName(e.target.value)
                  }
                />
                <button
                  type='button'
                  className='sop-btn-primary'
                  disabled={
                    readOnly ||
                    busy ||
                    isUpToDate ||
                    !saveName.trim() ||
                    selectedCount === 0
                  }
                  title={readOnly ? 'Requires a Business license' : undefined}
                  onClick={requestSaveAndApply}
                >
                  {isUpToDate ? 'Applied' : 'Save and Apply'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {status === 'done' && otherRules.length > 0 && (
        <div className='sop-other'>
          <button
            type='button'
            className={`sop-other-toggle${otherOpen ? ' is-open' : ''}`}
            aria-expanded={otherOpen}
            onClick={() => setOtherOpen((open) => !open)}
          >
            <IconChevronDown />
            <span className='sop-other-title'>
              Other rules in this SOP (
              {q
                ? `${visibleOtherRules.length} of ${otherRules.length}`
                : otherRules.length}
              )
            </span>
            <span className='sop-other-hint'>
              Found in the document, but BITE can&apos;t apply them
              automatically
            </span>
          </button>
          {otherOpen && (
            <div className='sop-rule-list'>
              {visibleOtherRules.length === 0 && (
                <div className='sop-empty'>
                  No other rules match your search.
                </div>
              )}
              {visibleOtherRules.map((r) => {
                const isOpen = openQuote === r.id;
                return (
                  <div key={r.id} className='sop-rule is-other'>
                    <div className='sop-rule-title'>{r.title}</div>
                    {r.detail && (
                      <div className='sop-rule-detail'>{r.detail}</div>
                    )}
                    <div className='sop-rule-reason'>
                      <span>Why it can&apos;t be applied:</span> {r.reason}
                    </div>
                    <SourceToggle
                      rule={r}
                      open={isOpen}
                      onToggle={() => setOpenQuote(isOpen ? null : r.id)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className='sop-saved'>
        <div className='sop-saved-title'>Saved rulesets</div>
        {runningJob && (
          <div className='sop-job' role='status'>
            <div className='sop-spinner' />
            <div className='sop-job-text'>
              {RULESET_JOB_VERBS[runningJob.action]} “{runningJob.name}”…
              <span className='sop-job-progress'>
                {runningJob.total > 0
                  ? ` ${runningJob.processed.toLocaleString()} of ${runningJob.total.toLocaleString()} products`
                  : ' Starting'}
              </span>
            </div>
          </div>
        )}
        <div className='sop-saved-list'>
          {rulesets.map((r) => (
            <div key={r.id} className='sop-saved-row'>
              <div className='sop-saved-text'>
                <div className='sop-saved-name'>
                  {r.name}
                  {r.is_active && (
                    <span className='sop-badge-active'>Active</span>
                  )}
                  {status === 'done' && r.id === editingId && (
                    <span className='sop-badge-editing'>Editing</span>
                  )}
                </div>
                <div className='sop-saved-meta'>
                  {plural(r.rules.length, 'rule')} ·{' '}
                  {r.source_filename ?? 'SOP'} · Saved{' '}
                  {formatRulesetDay(r.updated_at)}
                </div>
                {r.applied_at && (
                  <div className='sop-saved-summary'>
                    {r.is_active ? 'Applied' : 'Last applied'}{' '}
                    {formatRulesetDay(r.applied_at)}
                    {r.is_active && ` · ${rulesetStatusParts(r).join(' · ')}`}
                    {r.is_active && databaseId && (
                      <>
                        {' · '}
                        <button
                          type='button'
                          className='sop-link'
                          onClick={() => setChangesRuleset(r)}
                        >
                          View changes
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {status !== 'analyzing' &&
                r.id !== editingId &&
                (r.has_source_snapshot || r.source_extraction_id) && (
                  <button
                    type='button'
                    className='sop-btn-ghost'
                    disabled={loadingRulesetId !== null}
                    onClick={() => void loadRuleset(r)}
                  >
                    {loadingRulesetId === r.id ? 'Loading…' : 'Load'}
                  </button>
                )}
              <button
                type='button'
                className='sop-btn-ghost'
                disabled={readOnly || busy}
                title={readOnly ? 'Requires a Business license' : undefined}
                onClick={() =>
                  setPendingApply({
                    mode: 'apply',
                    rulesetId: r.id,
                    name: r.name,
                    ruleCount: r.rules.length,
                  })
                }
              >
                {r.is_active ? 'Re-apply' : 'Apply'}
              </button>
              {r.is_active && (
                <button
                  type='button'
                  className='sop-btn-ghost'
                  disabled={readOnly || busy}
                  title={readOnly ? 'Requires a Business license' : undefined}
                  onClick={() =>
                    setPendingRemoval({ action: 'deactivate', ruleset: r })
                  }
                >
                  Turn off
                </button>
              )}
              <button
                type='button'
                className='sop-btn-ghost'
                disabled={readOnly}
                title={readOnly ? 'Requires a Business license' : undefined}
                onClick={() => void openCopy(r)}
              >
                Copy to…
              </button>
              <button
                type='button'
                className='sop-icon-btn'
                title='Delete'
                aria-label={`Delete ${r.name}`}
                disabled={readOnly || busy}
                onClick={() => requestDelete(r)}
              >
                <IconX />
              </button>
            </div>
          ))}
          {rulesetsLoading && rulesets.length === 0 && (
            <div className='sop-empty'>Loading saved rulesets…</div>
          )}
          {!rulesetsLoading && rulesetsError && (
            <div className='sop-empty'>
              {rulesetsError}{' '}
              <button
                type='button'
                className='sop-btn-ghost'
                onClick={() => void loadRulesets()}
              >
                Try again
              </button>
            </div>
          )}
          {!rulesetsLoading && !rulesetsError && rulesets.length === 0 && (
            <div className='sop-empty'>
              No saved rulesets yet. Select rules above, then save and apply
              them.
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={!!pendingApply}
        onOpenChange={(open) => {
          if (!open) setPendingApply(null);
        }}
      >
        {pendingApply && (
          <DialogContent size='md' onClose={() => setPendingApply(null)}>
            <DialogHeader>
              <DialogTitle>
                {pendingApply.mode === 'update'
                  ? 'Update and apply'
                  : pendingApply.rulesetId &&
                    activeRuleset?.id === pendingApply.rulesetId
                  ? 'Re-apply'
                  : 'Apply'}{' '}
                “{pendingApply.name}”?
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className='ih-text-sm ih-text-muted'>
                {plural(pendingApply.ruleCount, 'rule')} will be applied to{' '}
                <strong>{target}</strong>. Products will be checked against
                these rules and updated where they don’t comply.
              </p>
              <p className='ih-text-sm ih-text-muted ih-mt-2'>
                {manualChangeNote(
                  pendingApply.mode === 'apply' &&
                    activeRuleset?.id === pendingApply.rulesetId
                    ? activeRuleset?.manual_changes ?? 0
                    : 0,
                )}
              </p>
              {pendingApply.mode === 'update' && (
                <p className='ih-text-sm ih-text-muted ih-mt-2'>
                  Your changes replace the saved version of this ruleset.
                </p>
              )}
              {activeRuleset && activeRuleset.id !== pendingApply.rulesetId && (
                <p className='ih-text-sm ih-text-muted ih-mt-2'>
                  This replaces the active ruleset,{' '}
                  <strong>{activeRuleset.name}</strong>; the changes it made are
                  undone first.
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant='ghost' onClick={() => setPendingApply(null)}>
                Cancel
              </Button>
              <Button
                variant='primary'
                locked={readOnly}
                onClick={() => void confirmApply()}
              >
                {pendingApply.mode === 'apply' ? 'Apply' : 'Save and Apply'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={!!copySource}
        onOpenChange={(open) => {
          if (!open && !copying) setCopySource(null);
        }}
      >
        {copySource && (
          <DialogContent size='md' onClose={() => setCopySource(null)}>
            <DialogHeader>
              <DialogTitle>
                Copy “{copySource.name}” to another database
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              {copyDatabases === null && !copyError ? (
                <p className='ih-text-sm ih-text-muted'>Loading databases…</p>
              ) : copyDatabases && copyDatabases.length === 0 ? (
                <p className='ih-text-sm ih-text-muted'>
                  This team has no other Import Hub databases to copy to.
                </p>
              ) : (
                copyDatabases && (
                  <label className='sop-copy-field'>
                    <span>Database</span>
                    <Select
                      value={copyTargetId}
                      onValueChange={setCopyTargetId}
                      disabled={copying}
                    >
                      {copyDatabases.map((database) => (
                        <option key={database.id} value={database.id}>
                          {database.name}
                        </option>
                      ))}
                    </Select>
                  </label>
                )
              )}
              <p className='ih-text-sm ih-text-muted ih-mt-2'>
                The copy is saved inactive with the same{' '}
                {plural(copySource.rules.length, 'rule')}. Nothing changes in
                that database until someone applies it there.
              </p>
              {copyError && (
                <p className='sop-file-error' role='alert'>
                  {copyError}
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                variant='ghost'
                onClick={() => setCopySource(null)}
                disabled={copying}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                locked={readOnly}
                disabled={copying || !copyTargetId}
                onClick={() => void confirmCopy()}
              >
                {copying ? 'Copying…' : 'Copy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={!!pendingRemoval}
        onOpenChange={(open) => {
          if (!open) setPendingRemoval(null);
        }}
      >
        {pendingRemoval && (
          <DialogContent size='md' onClose={() => setPendingRemoval(null)}>
            <DialogHeader>
              <DialogTitle>
                {pendingRemoval.action === 'delete' ? 'Delete' : 'Turn off'} “
                {pendingRemoval.ruleset.name}”?
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p className='ih-text-sm ih-text-muted'>
                Products in <strong>{target}</strong> go back to the FTAs and
                exclusions they had before this ruleset, except codes someone
                has changed since.
              </p>
              {pendingRemoval.action === 'delete' && (
                <p className='ih-text-sm ih-text-muted ih-mt-2'>
                  The ruleset is deleted once that&apos;s done.
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant='ghost' onClick={() => setPendingRemoval(null)}>
                Cancel
              </Button>
              <Button
                variant={
                  pendingRemoval.action === 'delete' ? 'danger' : 'primary'
                }
                locked={readOnly}
                onClick={confirmRemoval}
              >
                {pendingRemoval.action === 'delete' ? 'Delete' : 'Turn off'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
      {databaseId && (
        <RulesetChangesDialog
          databaseId={databaseId}
          ruleset={changesRuleset}
          onClose={() => setChangesRuleset(null)}
        />
      )}
    </div>
  );
};

export default SOP;
